import React, { useMemo, useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { Modal } from '../common';
import { useChatStore } from '@auw/stores';
import { useFeatureFlags, type SettingsTabFeatureId } from '@auw/feature-flags';
import {
  LayoutRow,
  SideNav,
  NavItem,
  ScrollFadeWrap,
  ScrollFadeMask,
  ContentArea,
  CustomScrollTrack,
  CustomScrollThumb,
} from './styles';
import { TABS, type SettingsTab } from './types';
import { ProfileTab } from './tabs/ProfileTab';
import { SubscriptionTab } from './tabs/SubscriptionTab';
import { BillingTab } from './tabs/BillingTab';
import { ServiceTab } from './tabs/ServiceTab';
import { ContactTab } from './tabs/ContactTab';

/* Phase 11-5: per-tab tenant gate.  Each tab in `TABS` is mapped to a
 * `page.settings.<tab>` flag so on-prem tenants without billing /
 * contact-sales surfaces simply hide those entries.  The map is keyed
 * by `SettingsTab` to keep enumeration exhaustive at build time. */
const TAB_FEATURE_ID: Record<SettingsTab, SettingsTabFeatureId> = {
  profile: 'page.settings.profile',
  subscription: 'page.settings.subscription',
  billing: 'page.settings.billing',
  service: 'page.settings.service',
  contact: 'page.settings.contact',
};

const MIN_THUMB_HEIGHT = 40;

interface SettingsModalProps {
  open: boolean;
  initialTab?: SettingsTab;
  /* Phase 11-4h: widened from the literal `'interests'` to `string` so
   * the host component matches the shared `SettingsModalProps` DI slot
   * shape (which must accept any tenant-specific focus section).  The
   * only B2C consumer (`ProfileTab`) still narrows via `=== 'interests'`
   * so this change is type-widening only. */
  focusSection?: string;
  requestId?: number;
  onClose: () => void;
}

export const SettingsModal: React.FC<SettingsModalProps> = ({
  open,
  initialTab,
  focusSection,
  requestId,
  onClose,
}) => {
  const { t } = useTranslation('settings');
  const theme = useTheme();
  const sidebarOpen = useChatStore((s) => s.sidebarOpen);
  const { isEnabled } = useFeatureFlags();

  /* Filter the static `TABS` list down to only those the active
   * tenant has enabled.  The first remaining tab becomes the default
   * if `initialTab` is missing or itself disabled. */
  const visibleTabs = useMemo(
    () => TABS.filter((tab) => isEnabled(TAB_FEATURE_ID[tab.id])),
    [isEnabled],
  );
  const fallbackTab: SettingsTab = visibleTabs[0]?.id ?? 'profile';
  const [activeTab, setActiveTab] = useState<SettingsTab>(fallbackTab);
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);
  const [thumbMetrics, setThumbMetrics] = useState({
    top: 0,
    height: 0,
    visible: false,
  });
  const [thumbDragging, setThumbDragging] = useState(false);
  const dragStateRef = useRef<{
    pointerId: number;
    startY: number;
    startScrollTop: number;
  } | null>(null);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const media = window.matchMedia(`(max-width: ${theme.breakpoints.mobile})`);
    const syncViewport = () => setIsMobileViewport(media.matches);
    syncViewport();
    media.addEventListener('change', syncViewport);
    return () => media.removeEventListener('change', syncViewport);
  }, [theme.breakpoints.mobile]);

  React.useEffect(() => {
    if (!open) return;
    /* Phase 11-5: honour the requested tab only when it's still
     * tenant-enabled.  Otherwise fall through to the first visible
     * tab so the modal never renders an empty pane. */
    const requested = initialTab ?? fallbackTab;
    setActiveTab(
      visibleTabs.some((tab) => tab.id === requested) ? requested : fallbackTab,
    );
  }, [initialTab, open, requestId, visibleTabs, fallbackTab]);

  const syncScrollState = useCallback(() => {
    const el = scrollRef.current;
    const track = trackRef.current;
    if (!el) return;
    const threshold = 4;
    const { scrollTop, clientHeight, scrollHeight } = el;
    setCanScrollUp(scrollTop > threshold);
    setCanScrollDown(scrollTop + clientHeight < scrollHeight - threshold);

    const scrollable = scrollHeight > clientHeight + 1;
    const trackHeight = track?.clientHeight ?? clientHeight;
    if (!scrollable || trackHeight <= 0) {
      setThumbMetrics((prev) =>
        prev.visible ? { top: 0, height: 0, visible: false } : prev,
      );
      return;
    }
    const ratio = clientHeight / scrollHeight;
    const height = Math.max(MIN_THUMB_HEIGHT, trackHeight * ratio);
    const maxTop = Math.max(0, trackHeight - height);
    const scrollRange = scrollHeight - clientHeight;
    const top = scrollRange > 0 ? (scrollTop / scrollRange) * maxTop : 0;
    setThumbMetrics((prev) => {
      if (
        prev.visible &&
        Math.abs(prev.top - top) < 0.5 &&
        Math.abs(prev.height - height) < 0.5
      ) {
        return prev;
      }
      return { top, height, visible: true };
    });
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !open) return;
    el.addEventListener('scroll', syncScrollState, { passive: true });
    const ro = new ResizeObserver(syncScrollState);
    ro.observe(el);
    if (trackRef.current) ro.observe(trackRef.current);
    const mo = new MutationObserver(syncScrollState);
    mo.observe(el, { childList: true, subtree: true });
    syncScrollState();
    return () => {
      el.removeEventListener('scroll', syncScrollState);
      ro.disconnect();
      mo.disconnect();
    };
  }, [open, syncScrollState, activeTab]);

  const handleThumbPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const el = scrollRef.current;
      if (!el) return;
      e.preventDefault();
      e.stopPropagation();
      dragStateRef.current = {
        pointerId: e.pointerId,
        startY: e.clientY,
        startScrollTop: el.scrollTop,
      };
      setThumbDragging(true);
      e.currentTarget.setPointerCapture(e.pointerId);
    },
    [],
  );

  const handleThumbPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const state = dragStateRef.current;
      const el = scrollRef.current;
      const track = trackRef.current;
      if (!state || !el || !track || state.pointerId !== e.pointerId) return;
      const trackHeight = track.clientHeight;
      const { clientHeight, scrollHeight } = el;
      const ratio = clientHeight / scrollHeight;
      const thumbH = Math.max(MIN_THUMB_HEIGHT, trackHeight * ratio);
      const maxTop = Math.max(0, trackHeight - thumbH);
      if (maxTop === 0) return;
      const scrollRange = scrollHeight - clientHeight;
      const deltaY = e.clientY - state.startY;
      const deltaScroll = (deltaY / maxTop) * scrollRange;
      el.scrollTop = Math.min(
        scrollRange,
        Math.max(0, state.startScrollTop + deltaScroll),
      );
    },
    [],
  );

  const endThumbDrag = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const state = dragStateRef.current;
      if (!state || state.pointerId !== e.pointerId) return;
      dragStateRef.current = null;
      setThumbDragging(false);
      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
    },
    [],
  );

  const handleTrackPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.target !== e.currentTarget) return;
      const el = scrollRef.current;
      const track = trackRef.current;
      if (!el || !track) return;
      const rect = track.getBoundingClientRect();
      const relativeY = e.clientY - rect.top;
      const thumbH = thumbMetrics.height;
      const targetTop = Math.max(0, Math.min(rect.height - thumbH, relativeY - thumbH / 2));
      const maxTop = Math.max(0, rect.height - thumbH);
      const scrollRange = el.scrollHeight - el.clientHeight;
      if (maxTop === 0 || scrollRange <= 0) return;
      el.scrollTop = (targetTop / maxTop) * scrollRange;
    },
    [thumbMetrics.height],
  );

  const centerPaddingLeft = !isMobileViewport && sidebarOpen
    ? Number.parseInt(theme.sizes.sidebarWidth, 10) || 0
    : 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('settings:title')}
      width="780px"
      centerPaddingLeft={centerPaddingLeft}
    >
      <LayoutRow>
        <SideNav>
          {visibleTabs.map((tab) => (
            <NavItem
              key={tab.id}
              $active={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
            >
              {t(tab.labelKey)}
            </NavItem>
          ))}
        </SideNav>
        <ScrollFadeWrap>
          <ScrollFadeMask $position="top" $visible={canScrollUp} />
          <ContentArea ref={scrollRef}>
            {activeTab === 'profile' && isEnabled('page.settings.profile') && (
              <ProfileTab focusSection={focusSection} focusRequestId={requestId} />
            )}
            {activeTab === 'subscription' && isEnabled('page.settings.subscription') && <SubscriptionTab />}
            {activeTab === 'billing' && isEnabled('page.settings.billing') && <BillingTab />}
            {activeTab === 'service' && isEnabled('page.settings.service') && <ServiceTab onClose={onClose} />}
            {activeTab === 'contact' && isEnabled('page.settings.contact') && <ContactTab />}
          </ContentArea>
          <ScrollFadeMask $position="bottom" $visible={canScrollDown} />
          <CustomScrollTrack
            ref={trackRef}
            $visible={thumbMetrics.visible}
            onPointerDown={handleTrackPointerDown}
          >
            <CustomScrollThumb
              $dragging={thumbDragging}
              style={{
                top: `${thumbMetrics.top}px`,
                height: `${thumbMetrics.height}px`,
              }}
              onPointerDown={handleThumbPointerDown}
              onPointerMove={handleThumbPointerMove}
              onPointerUp={endThumbDrag}
              onPointerCancel={endThumbDrag}
            />
          </CustomScrollTrack>
        </ScrollFadeWrap>
      </LayoutRow>
    </Modal>
  );
};
